import React from 'react';

/**
 * The Products design language, in one place (2026-08-29 card redesign).
 *
 * Inline stroke icons rather than emoji: emoji render at wildly different
 * sizes per platform and carry their own colour, so a column of them never
 * lines up and they cannot follow the theme. These inherit currentColor and
 * sit on the same 14px box, which is what makes a field grid read as a grid.
 *
 * Shared by the product card AND the bulk-update panel so the two cannot
 * drift apart (Rhys 2026-08-29 — the panel was still on emoji tabs).
 */
const iconBase = {
  width: 18,
  height: 18,
  viewBox: '0 0 20 20',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  style: { flexShrink: 0 }
};

export const IconBarcode = () => (
  <svg {...iconBase}><path d="M3 4v12M6 4v12M9 4v9M12 4v12M15 4v9M17.5 4v12" /></svg>
);

export const IconWeight = () => (
  <svg {...iconBase}><path d="M7 6a3 3 0 1 1 6 0" /><path d="M5.2 6h9.6l1.7 10.2a1 1 0 0 1-1 1.2H4.5a1 1 0 0 1-1-1.2Z" /></svg>
);

export const IconBox = () => (
  <svg {...iconBase}><path d="M10 2.5 17 6v8l-7 3.5L3 14V6Z" /><path d="M3 6l7 3.5L17 6" /><path d="M10 9.5v8" /></svg>
);

export const IconTag = () => (
  <svg {...iconBase}><path d="M10.5 2.5H17v6.5l-7.8 7.8a1.4 1.4 0 0 1-2 0l-4.5-4.5a1.4 1.4 0 0 1 0-2Z" /><circle cx="13.6" cy="6.4" r="1.1" /></svg>
);

/**
 * A WAREHOUSE (pitched roof over roller-door bays), not the house glyph the
 * mockup used: an overflow location is a warehouse bay, and staff read a
 * house as "home/default" (Rhys 2026-08-29).
 */
export const IconWarehouse = () => (
  <svg {...iconBase}><path d="M2.5 8.5 10 3.5l7.5 5v9h-15Z" /><path d="M6.5 17.5v-5h7v5" /><path d="M6.5 14.5h7" /></svg>
);

export const IconPin = () => (
  <svg {...iconBase}><path d="M10 17.5s5.5-4.6 5.5-8.5a5.5 5.5 0 1 0-11 0c0 3.9 5.5 8.5 5.5 8.5Z" /><circle cx="10" cy="9" r="2" /></svg>
);

/** Calendar page: food expiry (bulk tab, card toggle). */
export const IconCalendar = () => (
  <svg {...iconBase}><rect x="3" y="4.5" width="14" height="12.5" rx="1.6" /><path d="M3 8.5h14" /><path d="M7 2.8v3.2M13 2.8v3.2" /></svg>
);

/** One box opening into two: a kit split ("Breaks into" / "Part of kit"). */
export const IconKit = () => (
  <svg {...iconBase}><rect x="6.5" y="2.5" width="7" height="5" rx="1" /><path d="M10 7.5v2.5M5 12.5V10h10v2.5" /><rect x="2.5" y="12.5" width="5" height="5" rx="1" /><rect x="12.5" y="12.5" width="5" height="5" rx="1" /></svg>
);

/** Two chain links: Shopify related / complementary products (product links). */
export const IconLink = () => (
  <svg {...iconBase}><path d="M8.6 11.4a3.2 3.2 0 0 0 4.5 0l2.6-2.6a3.2 3.2 0 0 0-4.5-4.5l-1 1" /><path d="M11.4 8.6a3.2 3.2 0 0 0-4.5 0l-2.6 2.6a3.2 3.2 0 0 0 4.5 4.5l1-1" /></svg>
);

export const IconAlert = () => (
  <svg {...iconBase}><path d="M10 3.2 18 16.8H2Z" /><path d="M10 8.2v3.4" /><path d="M10 14.2h.01" /></svg>
);

/**
 * Trash, drawn at the checkbox's size rather than Polaris's micro button:
 * warehouse staff hit these on a touchscreen wearing gloves, and the micro
 * icon was a pinpoint target next to a 20px checkbox (Rhys 2026-08-29).
 */
export const IconTrash = ({ size = 20 }) => (
  <svg
    width={size} height={size} viewBox="0 0 20 20" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}
  >
    <path d="M3.5 5.5h13" />
    <path d="M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5" />
    <path d="M5.5 5.5 6.2 16a1.4 1.4 0 0 0 1.4 1.3h4.8a1.4 1.4 0 0 0 1.4-1.3l.7-10.5" />
    <path d="M8.6 8.7v5.1M11.4 8.7v5.1" />
  </svg>
);

/**
 * Tri-fold map. Drawn, not the emoji: U+1F5FA has no glyph in Segoe UI on the
 * warehouse machines and painted as a broken square (Rhys 2026-08-29).
 */
export const IconMap = ({ size = 14 }) => (
  <svg
    width={size} height={size} viewBox="0 0 20 20" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}
  >
    <path d="M7 3.5 3 5v11.5l4-1.5 6 1.5 4-1.5V3.5L13 5 7 3.5Z" />
    <path d="M7 3.5V15M13 5v16.5" transform="scale(1 0.7) translate(0 2)" />
  </svg>
);

export const IconPencil = ({ className }) => (
  <svg {...iconBase} className={className} width={15} height={15}><path d="M13.6 3.2l3.2 3.2L7 16.2l-4 .8.8-4Z" /><path d="M12 4.8l3.2 3.2" /></svg>
);

export const IconPrinter = () => (
  <svg {...iconBase}><path d="M6 8V3h8v5" /><path d="M6 14H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-2" /><path d="M6 12h8v5H6Z" /></svg>
);

/**
 * One label cell: icon + name, so every value in a column starts on the same
 * x. Replaces the old bold "Label:" text prefixes.
 */
export const FieldLabel = ({ icon, width, children }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
    color: 'var(--app-ink-secondary)',
    fontWeight: 600,
    // Fixed label column: values across rows start at the same x, which is
    // what makes the design's field list read as a table (Rhys 2026-08-29).
    ...(width ? { width, minWidth: width } : {})
  }}>
    {icon}
    <span>{children}</span>
  </span>
);

/**
 * Shared chrome for every click-to-edit cell, so "editing", "unsaved" and
 * "missing" look identical wherever they appear. Was six near-identical
 * ternary stacks inline on the card.
 */
export function fieldShell({ editing, dirty, missing, darkMode }) {
  // While editing the ROW stays in its idle clothes - the blue chrome
  // belongs to the input alone (fieldInput below). Rhys 2026-08-29: boxing
  // the whole row read as "the block is selected", not "this value is open".
  const border = editing ? '1px solid transparent'
    : dirty ? '2px solid #16a34a'
        // Borderless when idle (Rhys 2026-08-29): the transparent border
        // keeps the row from shifting when a state border appears.
        : '1px solid transparent';
  const background = dirty && !editing
    ? (darkMode ? 'rgba(22, 163, 74, 0.2)' : '#f0fdf4')
    : 'transparent';
  const thickBorder = dirty;
  return {
    // Stacked cell (2026-08-29 3x2 grid): the label line sits on top and the
    // value line underneath - children flow as rows.
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: '4px',
    minWidth: 0,
    // 2px state borders eat 1px more than the idle 1px transparent one -
    // compensate so values stay on the same x whatever the cell's state.
    padding: thickBorder ? '6px 11px' : '7px 12px',
    fontSize: '13px',
    minHeight: '48px',
    border,
    borderRadius: '6px',
    background,
    cursor: editing ? 'default' : 'pointer',
    color: 'var(--app-ink)'
  };
}

/**
 * The input that pops into a row's value slot while editing: a real input at
 * a comfortable size, carrying the blue chrome the row no longer paints.
 */
export function fieldInput(darkMode) {
  return {
    flex: 1,
    minWidth: 0,
    padding: '5px 9px',
    fontSize: '13px',
    border: '2px solid #2563eb',
    borderRadius: '7px',
    outline: 'none',
    background: darkMode ? '#111827' : '#ffffff',
    color: darkMode ? '#e5e7eb' : '#202223'
  };
}

/** Teal is the Products accent (matches LOCATE and the label dialog). */
export const ACCENT = { light: '#0d9488', dark: '#5eead4' };
